import { t } from "i18next";
import { useState } from "react";
import { Ban, CheckCircle2, PauseCircle } from "lucide-react";

import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useSetSalesAgentStatus } from "@/features/merchant/api/use-set-sales-agent-status";
import type { SalesAgentRow } from "@/features/merchant/components/merchant-sales-agents/sales-agents-columns";
import type { SalesAgentStatus } from "@/features/shared/api/enums";

type ActionableStatus = "ACTIVE" | "INACTIVE" | "SUSPENDED";

const nextStatuses = (status?: SalesAgentStatus): ActionableStatus[] => {
  switch (status) {
    case "ACTIVE":
      return ["INACTIVE", "SUSPENDED"];
    case "INACTIVE":
      return ["ACTIVE", "SUSPENDED"];
    case "SUSPENDED":
      return ["ACTIVE"];
    default:
      return [];
  }
};

const LABELS: Record<ActionableStatus, string> = {
  ACTIVE: "merchant.salesAgents.actions.activate",
  INACTIVE: "merchant.salesAgents.actions.deactivate",
  SUSPENDED: "merchant.salesAgents.actions.suspend",
};

const ICONS: Record<ActionableStatus, typeof Ban> = {
  ACTIVE: CheckCircle2,
  INACTIVE: PauseCircle,
  SUSPENDED: Ban,
};

export function SalesAgentRowActions({
  agent,
  merchantId,
}: {
  agent: SalesAgentRow;
  merchantId: string;
}) {
  const setStatus = useSetSalesAgentStatus(merchantId);
  const [targetStatus, setTargetStatus] = useState<ActionableStatus | null>(
    null
  );

  const actions = nextStatuses(agent.status).map((status) => ({
    id: `set-status-${status}`,
    label: t(LABELS[status]),
    icon: ICONS[status],
    variant:
      status === "SUSPENDED" ? ("destructive" as const) : ("default" as const),
    onClick: () => setTargetStatus(status),
  }));

  if (actions.length === 0) return null;

  return (
    <>
      <RowActionsMenu actions={actions} />
      <MutationConfirmPreview
        open={targetStatus !== null}
        onOpenChange={(open) => {
          if (!open) setTargetStatus(null);
        }}
        data={{
          id: agent.id,
          fullName: agent.fullName,
          currentStatus: agent.status,
          nextStatus: targetStatus,
        }}
        title={t("common.mutationPreview.statusTitle")}
        description={t("common.mutationPreview.statusDescription")}
        confirmLabel={targetStatus ? t(LABELS[targetStatus]) : undefined}
        t={t}
        pending={setStatus.isPending}
        onConfirm={() => {
          if (!targetStatus) return;
          setStatus.mutate(
            { salesAgentId: agent.id, status: targetStatus },
            {
              onSuccess: () => {
                notifSilence(t("merchant.salesAgents.feedback.statusUpdated"));
                setTargetStatus(null);
              },
              onError: (error) => notifSilenceError(error.message),
            }
          );
        }}
      />
    </>
  );
}
